import { CompositeFilterDescriptor, DataSourceRequestState, FilterDescriptor, State as GridState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { GridColumn as Column, Grid, GridToolbar } from '@progress/kendo-react-grid';
import { plusIcon } from '@progress/kendo-svg-icons';
import { GridLoadingPanel } from 'components/GridLoadingPanel';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { debounce } from 'ts-debounce';
import { DropdownFilterCellIDName } from '../../components/cells/DropdownFilterCellIDName';
import EmailCell from '../../components/cells/EmailCell';
import PhoneCellAndExtension from '../../components/cells/PhoneCellAndExtension';
import { fetchApi } from '../../services/api';
import { Title } from '../../utils/title';
import AddCustomerContactDialog from './AddCustomerContactDialog';
import { contactTypeOptions } from './ContactTypeOptions';
import { ContactTypeNameCell, CustomerContactCell, CustomerContactViewModel } from './CustomerContactGrid';
import { CustomerNavBar } from './NavBar';

type RouteComponentParams = {
  customerId: string;
};

type CustomerContactResult = {
  Data: CustomerContactViewModel[],
  Total: number,
}

const resetDataState = {
  skip: 0,
  take: 50,
  sort: [{
    field: "FullName",
    dir: "asc"
  }],
} as GridState;

const defaultAndFilter = {
  logic: 'and', filters: [
    { field: 'BusinessUnitID', operator: 'eq', value: 2610 },
    { field: 'Active', operator: 'eq', value: true }
  ]
} as CompositeFilterDescriptor;

const CustomerContacts = () => {
  const { customerId: customerIdParam } = useParams<RouteComponentParams>();
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(true);
  const [dataState, setDataState] = useState<DataSourceRequestState>({ ...resetDataState, filter: { ...defaultAndFilter } });

  const [customerContacts, setCustomerContacts] = useState<CustomerContactResult>();
  const [showAddContactDialog, setShowAddContactDialog] = useState(false);

  const history = useHistory();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFilter = useCallback(
    debounce((func: () => void) => {
      func();
    }, 750), []);

  const refresh = useCallback(() => {
    if (query) {
      setLoading(true);
      const data = {
        //Active: true,
        CustomerID: customerIdParam
      };

      const queryStr = `${toDataSourceRequestString(dataState)}`;
      fetchApi(`/api/Customer/CustomerContacts?${queryStr}`, data, 'POST')
        .then(data => {
          setCustomerContacts(data);
        })
        .catch(e => {
          // If not problem details
          if (!e?.status) alert(e);
        })
        .finally(() => setLoading(false));
    }
  }, [customerIdParam, dataState, query]);

  useEffect(() => {
    refresh();
  }, [customerIdParam, refresh]);

  return <React.Fragment>
    <CustomerNavBar id={customerIdParam} currentTab="Contacts" />
    <Title string="Contacts" />
    {showAddContactDialog && <AddCustomerContactDialog
      CustomerId={Number(customerIdParam)}
      CloseDialog={(newCustomerContactId) => {
        if (newCustomerContactId > 0) history.push(`/Customers/Customer/Contact/${newCustomerContactId}`);
        setShowAddContactDialog(false);
      }}
    />}
    {loading && <GridLoadingPanel />}
    <Grid
      {...dataState}
      data={customerContacts?.Data}
      total={customerContacts?.Total}
      sortable
      pageable
      filterable
      onDataStateChange={(e) => { setQuery(false); setDataState(e.dataState); debounceFilter(() => { setQuery(true) }); }}
    >
      <GridToolbar>
        <Button
          icon="plus"
          svgIcon={plusIcon}
          themeColor="primary"
          onClick={() => setShowAddContactDialog(true)}
        > Contact
        </Button>
        <Button
          className="ml-1"
          themeColor={((dataState.filter as CompositeFilterDescriptor).filters as FilterDescriptor[]).find(x => x.field === 'Active').value === true ? 'primary' : 'warning'}
          onClick={() => {
            const activeFilter = ((dataState.filter as CompositeFilterDescriptor).filters as FilterDescriptor[]).find(x => x.field === 'Active');
            activeFilter.value = !(activeFilter.value as boolean);
            setDataState({ ...dataState });
          }}
        >{((dataState.filter as CompositeFilterDescriptor).filters as FilterDescriptor[]).find(x => x.field === 'Active').value === true ? 'Active' : 'Retired'}</Button>
      </GridToolbar>
      <Column field="FullName" title="Name" cells={{ data: CustomerContactCell }} />
      <Column field="EMailAddress" title="E-Mail" cells={{ data: EmailCell }} />
      <Column field="PhoneNumber" title="Phone" cells={{ data: PhoneCellAndExtension }} />
      <Column
        field="ContactType"
        title="Contact Type"
        cells={{
          data: ContactTypeNameCell,
          filterCell: (props) => (
            <DropdownFilterCellIDName
              {...props}
              data={contactTypeOptions}
            />
          )
        }}
      />
      <Column field="Title" title="Title" />
      {/* <Column field="CustomerNumber" title="Customer" cells={{ data: CustomerCell }} filterable={false} /> */}
    </Grid>
  </React.Fragment>;
}

export default CustomerContacts;
